import * as React from 'react';
import truncate from 'lodash/truncate';
import debounce from 'lodash/debounce';

import classify from 'src/utils/classify';

import {MouseTip} from 'src/components/lib/mouse-tip/mouse-tip';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';

import css from './truncated-text.css';


type TruncatedTextType = (<C extends string>(arg1: {
  [key: string]: unknown;
  text: string;
  limit: number;
  component?: C;
}) => React.ReactNode) &
  (<C extends React.ComponentType<any>>(
    arg1: React.ComponentProps<C> & {
      text: string;
      limit: number;
      component: C;
    },
  ) => React.ReactNode);

const TruncatedText: TruncatedTextType = ({
  // @ts-expect-error - TS7031 - Binding element 'text' implicitly has an 'any' type.
  text,
  // @ts-expect-error - TS7031 - Binding element 'limit' implicitly has an 'any' type.
  limit,
  component = 'span',
  ...props
}) => {
  const shortText = truncate(text, {length: limit});
  const content = React.createElement(component, props, shortText);
  if (shortText === text) {
    return content;
  }
  return <MouseTip content={text}>{content}</MouseTip>;
};

export default TruncatedText;

type AutoTruncatedTextType = (<C extends string>(arg1: {
  [key: string]: unknown;
  useFragment?: boolean;
  text: React.ReactNode;
  tipContent?: React.ReactNode;
  wrapText?: boolean;
  component?: C;
  className?: string;
}) => React.ReactNode) &
  (<C extends React.ComponentType<unknown>>(
    arg1: React.ComponentProps<C> & {
      useFragment?: boolean;
      text: React.ReactNode;
      tipContent?: React.ReactNode;
      wrapText?: boolean;
      component: C;
      className?: string;
    },
  ) => React.ReactNode);

// @ts-expect-error - TS2322 - Type '({ useFragment, text, tipContent, wrapText, className, ...props }: { [x: string]: any; useFragment?: boolean | undefined; text: any; tipContent: any; wrapText?: boolean | undefined; className: any; }) => Element' is not assignable to type 'AutoTruncatedTextType'.
export const AutoTruncatedText: AutoTruncatedTextType = ({
  useFragment = false,
  // @ts-expect-error - TS7031 - Binding element 'text' implicitly has an 'any' type.
  text,
  // @ts-expect-error - TS7031 - Binding element 'tipContent' implicitly has an 'any' type.
  tipContent,
  wrapText = false,
  // @ts-expect-error - TS7031 - Binding element 'className' implicitly has an 'any' type.
  className,
  ...props
}) => {
  const [showMouseTip, setShowMouseTip] = React.useState(false);
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const truncatedRef = React.useRef<HTMLSpanElement | null | undefined>();

  const checkWidths = React.useMemo(
    () =>
      debounce(() => {
        const span = truncatedRef.current;

        if (span) {
          setShowMouseTip(span.offsetWidth < span.scrollWidth);
        }
      }, 200),
    [],
  );

  //running only on mount and unmount
  React.useEffect(() => {
    window.addEventListener('resize', checkWidths);

    return () => {
      window.removeEventListener('resize', checkWidths);
    };
  }, [checkWidths]);

  React.useEffect(() => {
    checkWidths();
  }, [text]);

  const content = showMouseTip ? (
    <MouseTip content={tipContent ?? text}>
      {}
      <span
        {...props}
        className={classify(css.truncatedText, className)}
        // @ts-expect-error - TS2322 - Type 'RefObject<HTMLSpanElement | null | undefined>' is not assignable to type 'Ref<HTMLSpanElement> | undefined'.
        ref={truncatedRef}
      >
        {text}
      </span>
    </MouseTip>
  ) : (
    <span
      {...props}
      className={classify(className, {
        [css.truncatedText]: !wrapText,
        [css.wrapText]: wrapText,
      })}
      // @ts-expect-error - TS2322 - Type 'RefObject<HTMLSpanElement | null | undefined>' is not assignable to type 'Ref<HTMLSpanElement> | undefined'.
      ref={truncatedRef}
    >
      {text}
    </span>
  );

  return useFragment ? (
    <>{content}</>
  ) : (
    <div className={css.container}>{content}</div>
  );
};

// @ts-expect-error - TS2322 - Type '({ useFragment, text, tipContent, wrapText, className, ...props }: { [x: string]: any; useFragment?: boolean | undefined; text: any; tipContent: any; wrapText?: boolean | undefined; className: any; }) => Element' is not assignable to type 'AutoTruncatedTextType'.
export const AutoTruncatedTextGenesis: AutoTruncatedTextType = ({
  useFragment = false,
  // @ts-expect-error - TS7031 - Binding element 'text' implicitly has an 'any' type.
  text,
  // @ts-expect-error - TS7031 - Binding element 'tipContent' implicitly has an 'any' type.
  tipContent,
  wrapText = false,
  // @ts-expect-error - TS7031 - Binding element 'className' implicitly has an 'any' type.
  className,
  ...props
}) => {
  const [showMouseTip, setShowMouseTip] = React.useState(false);
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const truncatedRef = React.useRef<HTMLSpanElement | null | undefined>();

  const checkWidths = React.useMemo(
    () =>
      debounce(() => {
        const span = truncatedRef.current;

        if (span) {
          setShowMouseTip(span.offsetWidth < span.scrollWidth);
        }
      }, 200),
    [],
  );

  //running only on mount and unmount
  React.useEffect(() => {
    window.addEventListener('resize', checkWidths);

    return () => {
      window.removeEventListener('resize', checkWidths);
    };
  }, [checkWidths]);

  React.useEffect(() => {
    checkWidths();
  }, [text]);

  const content = showMouseTip ? (
    <Tooltip body={tipContent ?? text} elevation="toast" bodyMaxLines={10}>
      {}
      <span
        {...props}
        className={classify(css.truncatedText, className)}
        // @ts-expect-error - TS2322 - Type 'RefObject<HTMLSpanElement | null | undefined>' is not assignable to type 'Ref<HTMLSpanElement> | undefined'.
        ref={truncatedRef}
      >
        {text}
      </span>
    </Tooltip>
  ) : (
    <span
      {...props}
      className={classify(className, {
        [css.truncatedText]: !wrapText,
        [css.wrapText]: wrapText,
      })}
      // @ts-expect-error - TS2322 - Type 'RefObject<HTMLSpanElement | null | undefined>' is not assignable to type 'Ref<HTMLSpanElement> | undefined'.
      ref={truncatedRef}
    >
      {text}
    </span>
  );

  return useFragment ? (
    <>{content}</>
  ) : (
    <div className={css.container}>{content}</div>
  );
};
